import { useState, useRef, useEffect } from 'react';

export default function MagicDrawingPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  const [generatedImage, setGeneratedImage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('cartoon');

  const styles = [
    { id: 'cartoon', name: '卡通风格', icon: 'ri-emotion-happy-line' },
    { id: 'watercolor', name: '水彩画', icon: 'ri-drop-line' },
    { id: 'sketch', name: '素描', icon: 'ri-pencil-line' },
    { id: 'oil', name: '油画', icon: 'ri-palette-line' },
    { id: 'anime', name: '动漫', icon: 'ri-star-smile-line' },
    { id: 'realistic', name: '写实', icon: 'ri-camera-line' }
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    draw(e);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.strokeStyle = color;

    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setGeneratedImage('');
  };

  const generateArt = () => {
    setIsGenerating(true);
    
    // 模拟AI生成过程
    setTimeout(() => {
      const stylePrompts: Record<string, string> = {
        cartoon: 'colorful cartoon style illustration with bold outlines and vibrant colors, playful and fun, children friendly art',
        watercolor: 'soft watercolor painting with gentle brushstrokes, pastel colors, artistic and dreamy atmosphere',
        sketch: 'detailed pencil sketch drawing, black and white, artistic shading and fine lines',
        oil: 'classical oil painting style with rich textures, warm colors, museum quality art',
        anime: 'Japanese anime style illustration, big expressive eyes, dynamic pose, vibrant colors',
        realistic: 'photorealistic rendering, detailed textures, natural lighting, high quality photography'
      };

      const prompt = stylePrompts[selectedStyle];
      const width = 512;
      const height = 512;
      const seq = Date.now();
      
      setGeneratedImage(`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg${encodeURIComponent(prompt)}&width=${width}&height=${height}&seq=${seq}&orientation=squarish`);
      setIsGenerating(false);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/primary')}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-700"></i>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <i className="ri-brush-line text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">神奇画板</h1>
                  <p className="text-sm text-gray-600">用AI创作属于你的艺术作品</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Drawing Area */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">画板区域</h2>
              <p className="text-gray-600">在这里画出你的创意吧！</p>
            </div>

            {/* Canvas */}
            <div className="mb-6">
              <canvas
                ref={canvasRef}
                width={600}
                height={600}
                className="border-4 border-gray-200 rounded-xl cursor-crosshair w-full"
                onMouseDown={startDrawing}
                onMouseUp={stopDrawing}
                onMouseMove={draw}
                onMouseLeave={stopDrawing}
              />
            </div>

            {/* Tools */}
            <div className="space-y-4">
              {/* Color Picker */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-palette-line mr-2"></i>画笔颜色
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <div className="flex space-x-2">
                    {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF'].map((c) => (
                      <button
                        key={c}
                        onClick={() => setColor(c)}
                        className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:scale-110 transition-all cursor-pointer"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Brush Size */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-pencil-line mr-2"></i>画笔粗细: {brushSize}px
                </label>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={clearCanvas}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-eraser-line mr-2"></i>清空画板
                </button>
              </div>
            </div>
          </div>

          {/* AI Generation Area */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">AI魔法区域</h2>
              <p className="text-gray-600">选择风格，让AI帮你变成艺术品！</p>
            </div>

            {/* Style Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <i className="ri-magic-line mr-2"></i>选择艺术风格
              </label>
              <div className="grid grid-cols-2 gap-3">
                {styles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setSelectedStyle(style.id)}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedStyle === style.id
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                  >
                    <i className={`${style.icon} text-3xl ${selectedStyle === style.id ? 'text-pink-600' : 'text-gray-600'} mb-2`}></i>
                    <div className={`font-semibold ${selectedStyle === style.id ? 'text-pink-600' : 'text-gray-700'}`}>
                      {style.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateArt}
              disabled={isGenerating}
              className="w-full px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mb-6"
            >
              {isGenerating ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  AI正在创作中...
                </>
              ) : (
                <>
                  <i className="ri-magic-line mr-2"></i>
                  生成艺术作品
                </>
              )}
            </button>

            {/* Generated Image */}
            <div className="bg-gray-50 rounded-xl p-4 min-h-[400px] flex items-center justify-center">
              {generatedImage ? (
                <div className="w-full">
                  <img
                    src={generatedImage}
                    alt="AI生成的艺术作品"
                    className="w-full rounded-lg shadow-lg"
                  />
                  <div className="mt-4 flex space-x-3">
                    <button className="flex-1 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg font-semibold hover:bg-pink-200 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-download-line mr-2"></i>保存作品
                    </button>
                    <button className="flex-1 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-share-line mr-2"></i>分享作品
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-400">
                  <i className="ri-image-line text-6xl mb-4"></i>
                  <p>AI生成的作品将显示在这里</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <i className="ri-lightbulb-line text-blue-600 text-2xl mt-1"></i>
            <div>
              <h3 className="font-bold text-blue-900 mb-2">使用小贴士：</h3>
              <ul className="space-y-1 text-blue-800 text-sm">
                <li>• 先在左侧画板上画出你的创意草图</li>
                <li>• 选择你喜欢的艺术风格</li>
                <li>• 点击"生成艺术作品"按钮，让AI帮你创作</li>
                <li>• 可以多次尝试不同的风格哦！</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
